import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { card, input, label, btnPrimary } from "../../ui";
import { Sparkles, Loader2, RefreshCw } from "lucide-react";

const FIELDS = [
  ["main_complaint", "Queixa principal", 3],
  ["hypothesis", "Hipótese / diagnóstico", 2],
  ["treatment_plan", "Plano terapêutico", 3],
  ["medications", "Medicações em uso", 2],
  ["notes", "Observações gerais", 2],
];

export default function GeneralView({ patient, onSaved }) {
  const [form, setForm] = useState({
    main_complaint: patient.main_complaint || "",
    hypothesis: patient.hypothesis || "",
    treatment_plan: patient.treatment_plan || "",
    medications: patient.medications || "",
    notes: patient.notes || "",
  });
  const [summary, setSummary] = useState(patient.clinical_summary || "");
  const [saving, setSaving] = useState(false);
  const [organizing, setOrganizing] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/patients/${patient.id}`, {
        name: patient.name, email: patient.email || "", phone: patient.phone || "",
        email_enabled: patient.email_enabled !== false, ...form,
      });
      toast.success("Dados clínicos salvos.");
      onSaved?.();
    } catch { toast.error("Erro ao salvar."); }
    finally { setSaving(false); }
  };

  const organize = async () => {
    setOrganizing(true);
    try {
      const { data } = await api.post(`/patients/${patient.id}/clinical-summary`);
      setSummary(data.clinical_summary || "");
      toast.success("Resumo clínico geral atualizado pela IA. 🌿");
      onSaved?.();
    } catch { toast.error("Não consegui gerar o resumo agora."); }
    finally { setOrganizing(false); }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div className={`${card} p-6`} data-testid="clinical-summary-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="inline-flex items-center gap-2 font-display text-xl text-[#2C332A]">
            <Sparkles className="w-5 h-5 text-[#5B6E5B]" /> Resumo clínico geral (IA)
          </p>
          <button onClick={organize} disabled={organizing} data-testid="clinical-organize"
            className="inline-flex items-center gap-2 rounded-full bg-[#C87A5E] px-4 py-2 text-sm text-[#FDFBF7] hover:bg-[#B2644D] transition-colors disabled:opacity-60">
            {organizing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Organizar com IA
          </button>
        </div>
        {summary ? (
          <p className="font-body text-sm text-[#2C332A] mt-4 whitespace-pre-line leading-relaxed" data-testid="clinical-summary-text">{summary}</p>
        ) : (
          <p className="font-body text-sm text-[#7A8A7A] mt-4" data-testid="clinical-summary-empty">
            Ainda sem resumo. Ele é atualizado automaticamente a cada anotação na sessão, ou clique em &quot;Organizar com IA&quot;.
          </p>
        )}
      </div>

      <div className={`${card} p-6`} data-testid="clinical-form">
        <p className="font-display text-xl text-[#2C332A] mb-4">Dados clínicos atuais</p>
        <div className="space-y-4">
          {FIELDS.map(([k, lbl, rows]) => (
            <div key={k}>
              <label className={label}>{lbl}</label>
              <textarea rows={rows} className={`${input} resize-none`} value={form[k]} onChange={set(k)} data-testid={`clinical-${k}`} />
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-5">
          <button className={btnPrimary} onClick={save} disabled={saving} data-testid="clinical-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Salvar dados clínicos
          </button>
        </div>
      </div>
    </div>
  );
}
